import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { clearRestoreCache } from "@/lib/restore";
import { useAuth } from "@/context/AuthContext";

const NAMES = { filejump: "FileJump", onedrive: "OneDrive", plex: "Plex" };
const SOURCES = { filejump: "/webdav", onedrive: "/onedrive", plex: "/plex" };

/* Re-scans every connected storage, waits for the imports to finish, then reloads the current list. */
export const RefreshLibraryButton = ({ onDone, size = "md" }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  if (user?.role === "guest" && !user?.permissions?.upload_onedrive && !user?.permissions?.upload_plex) return null;
  const waitFor = async (keys) => {
    for (let i = 0; i < 120 && alive.current; i++) {
      await new Promise((r) => setTimeout(r, 1500));
      const states = await Promise.all(keys.map((k) => api.get(SOURCES[k]).then((r) => r.data.sync_status).catch(() => "idle")));
      if (!states.includes("running")) return;
    }
  };
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/library/refresh");
      const keys = [...data.started, ...data.busy];
      if (keys.length) {
        toast(`Re-scanning ${keys.map((k) => NAMES[k]).join(" + ")}…`);
        await waitFor(keys);
      }
      clearRestoreCache();
      await onDone?.();
      toast.success(keys.length ? "Library refreshed" : "Library reloaded", { description: keys.length ? "Storage re-scanned; anything removed from storage is gone from the library." : "No connected storage to re-scan — showing the latest from the database." });
    } catch (e) { toast.error(formatApiError(e)); }
    if (alive.current) setBusy(false);
  };
  const cls = size === "sm" ? "h-9 px-3 text-xs" : "h-10 px-4 text-sm";
  return (
    <button onClick={go} disabled={busy} title="Re-scan connected storage and reload" data-testid="library-refresh-button"
      className={`flex items-center gap-2 rounded-full border border-zinc-300 bg-white text-zinc-700 transition-colors hover:border-zinc-900 disabled:opacity-60 ${cls}`}>
      <RefreshCw size={14} className={busy ? "animate-spin" : ""} />{busy ? "Refreshing…" : "Refresh"}
    </button>
  );
};
